import { c as _c } from "react/compiler-runtime";
/**
 * Effective timezone resolution for non-hook callers.
 *
 * The tier state lives in `@/stores/timezone-store` (user-scoped, written from
 * committed effects). This module holds the pure resolution logic on top of it,
 * plus `getTimezone()` for API files and plain utilities. It imports no React
 * contexts/hooks, so it is *import*-safe from the server bundle (e.g. via
 * `format-data.tsx`) without tripping the "use client" boundary.
 *
 * IMPORT-SAFE IS NOT REQUEST-SAFE. The store is populated by the client
 * providers, which never run during a server render, and no client-side store
 * could hold per-request state anyway. `getTimezone()` therefore resolves to UTC
 * on the server rather than silently reporting the server process's zone, which
 * varies by host and belongs to nobody.
 *
 * Server code that formats a real clinic timestamp (route handlers rendering
 * email/print templates) must pass an explicit zone — resolved from the record's
 * location or the tenant default — to `formatDate`/`formatTime` rather than
 * relying on this getter. See `app/[tenant]/api/invoice-template/*`.
 *
 * The React hooks (`useTimezone`, `useTimezoneInfo`) live in
 * `@/hooks/use-timezone`. They resolve the same tiers through the same
 * `resolveTimezone` below, but read them reactively from context so a render
 * sees the current zone rather than one commit behind. The store is what keeps
 * the non-hook getter in step with them.
 */
import { tzOffsetMs } from "@/lib/timezone-dates";
import { useTimezoneStore } from "@/stores/timezone-store";
export const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * A malformed IANA name (mistyped tenant/location zone) makes
 * `Intl.DateTimeFormat` throw `RangeError`. Most formatters pass
 * `getTimezone()` straight to `toLocaleDateString`, so an invalid tier would
 * crash ordinary formatting. Validate here and drop invalid values to null so
 * the resolver falls through to the next tier (and getTimezone() only ever
 * returns a usable zone).
 */
/** True when `tz` is a usable IANA zone (i.e. Intl won't throw on it). */
export function isValidTimezone(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz
    });
    return true;
  } catch {
    return false;
  }
}
function validTimezoneOrNull(tz: string | null): string | null {
  if (isValidTimezone(tz)) return tz;
  if (tz) console.warn(`[timezone] ignoring invalid IANA timezone "${tz}"`);
  return null;
}

/**
 * The single resolution chain: override > current location > tenant default.
 * The tenant default counts when the tenant's locations share one timezone,
 * or when the selected location has no usable one of its own. Otherwise it is
 * one location's clock, and a viewer with no current location is better served
 * by their browser's. Returns null when no
 * tier applies, leaving the fallback to the caller (browser zone in the
 * client, UTC on the server).
 *
 * Shared by `getTimezone()` and `useTimezone()` so the store-backed and
 * context-backed paths can differ in timing but never in logic.
 */
export function resolveTimezone(tiers: {
  timezoneOverride?: string | null;
  currentLocationTimezone?: string | null;
  hasCurrentLocation?: boolean;
  tenantDefaultTimezone?: string | null;
  tenantSpansTimezones?: boolean;
}): string | null {
  return [tiers.timezoneOverride, tiers.currentLocationTimezone, tiers.tenantSpansTimezones && !tiers.hasCurrentLocation ? null : tiers.tenantDefaultTimezone].find(isValidTimezone) ?? null;
}

/** The first candidate that is a valid IANA zone name. */
export function firstValidTimezone(...candidates: (string | null | undefined)[]): string | undefined {
  return candidates.find(isValidTimezone);
}

/**
 * The clock a location's records are on: its own zone, or the tenant default
 * when it has none (as the resolver counts it). `fallback` is for a location
 * that isn't known at all.
 */
export function locationTimezone(location: {
  timezone?: string | null;
} | null | undefined, tenantDefaultTimezone: string | null | undefined, fallback: string): string {
  if (!location) return fallback;
  return firstValidTimezone(location.timezone, tenantDefaultTimezone) ?? fallback;
}
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
const CLOCK_WINDOW_DAYS = 371;
let clockKeyWeek: number | null = null;
const clockKeys = new Map<string, string>();

/**
 * Identifies a zone by its clock rather than its name: its offset at the start
 * of the year around now and every change of offset in that year, to the
 * minute. `America/Detroit` and `America/New_York` share one;
 * `America/Phoenix` and `America/Denver` don't, nor do two zones whose clocks
 * change a few days apart. Mirrors `clock_key` in `decoda/common/timezone.py`.
 * An invalid zone keys as its own name.
 */
export function clockKey(timeZone: string): string {
  const week = Math.floor(Date.now() / WEEK_MS);
  if (week !== clockKeyWeek) {
    clockKeys.clear();
    clockKeyWeek = week;
  }
  let key = clockKeys.get(timeZone);
  if (key === undefined) {
    key = isValidTimezone(timeZone) ? offsetChanges(timeZone, (week - 26) * WEEK_MS) : `invalid:${timeZone}`;
    clockKeys.set(timeZone, key);
  }
  return key;
}
function offsetChanges(timeZone: string, startMs: number): string {
  const offset = (ms: number) => tzOffsetMs(timeZone, new Date(ms));
  const parts = [String(offset(startMs))];
  let before = startMs;
  let beforeOffset = offset(startMs);
  for (let day = 1; day <= CLOCK_WINDOW_DAYS; day++) {
    const after = startMs + day * DAY_MS;
    const afterOffset = offset(after);
    if (afterOffset !== beforeOffset) {
      // Narrow the change to the minute it happens.
      let low = before;
      let high = after;
      while (high - low > 60_000) {
        const middle = low + Math.floor((high - low) / 2);
        if (offset(middle) === beforeOffset) low = middle;else high = middle;
      }
      parts.push(`${Math.floor(high / 60_000)}:${afterOffset}`);
    }
    before = after;
    beforeOffset = afterOffset;
  }
  return parts.join(",");
}

/** Whether two zones keep the same clock, whatever their names. */
export function sameClock(a: string, b: string): boolean {
  return a === b || clockKey(a) === clockKey(b);
}

/**
 * The current location's tiers for `resolveTimezone`. `locations` must include
 * archived ones: an archived current location keeps its own clock. `selected`
 * is true only for a known location, so a known zoneless one falls to the
 * tenant default while an unknown id counts as no selection.
 */
export function currentLocationTier(currentLocationId: string | null | undefined, locations: readonly {
  id: string;
  timezone?: string | null;
}[]): {
  timezone: string | null;
  selected: boolean;
} {
  const location = currentLocationId ? locations.find(candidate => candidate.id === currentLocationId) : undefined;
  return {
    timezone: location?.timezone || null,
    selected: !!location
  };
}

/**
 * The clock a report filtered to `locationIds` cuts its days on, as the server
 * resolves it (`resolve_locations_timezone`): the clock those locations share
 * (a location with none counts as the tenant default), named as the tenant
 * default when it keeps that clock, else as the first location's zone; else
 * `fallback`.
 * `fallback` too with no filter, or when none of the ids is a known location.
 */
export function locationsSharedTimezone(locationIds: readonly string[] | null | undefined, locations: readonly {
  id: string;
  timezone?: string | null;
}[], tenantDefaultTimezone: string | null | undefined, fallback: string): string {
  if (!locationIds?.length) return fallback;
  const wanted = new Set(locationIds);
  const zones = locations.filter(location => wanted.has(location.id)).map(location => locationTimezone(location, tenantDefaultTimezone, fallback));
  if (new Set(zones.map(clockKey)).size !== 1) return fallback;
  return isValidTimezone(tenantDefaultTimezone) && sameClock(tenantDefaultTimezone, zones[0]) ? tenantDefaultTimezone : zones[0];
}

/** A recurring series keeps the zone it was stamped with; `fallback` otherwise. */
export function seriesTimezone(series: {
  timezone?: string | null;
} | null | undefined, fallback: string): string {
  return firstValidTimezone(series?.timezone) ?? fallback;
}

/**
 * Whether active, in-person locations keep more than one clock (compared by
 * `clockKey`, so two names for one clock count once).
 * Virtual locations have no clinic clock of their own, so they don't count. A
 * location with no valid zone of its own keeps the tenant default's clock.
 */
export function locationsSpanTimezones(locations: {
  timezone?: string | null;
  isVirtual?: boolean;
  isArchived?: boolean;
}[], tenantDefaultTimezone?: string | null): boolean {
  const clocks = new Set(locations.filter(l => !l.isVirtual && !l.isArchived).map(l => isValidTimezone(l.timezone) ? l.timezone : tenantDefaultTimezone).filter(isValidTimezone).map(clockKey));
  return clocks.size > 1;
}

/** Scope the stored tiers to a user; changing the uid clears them. */
export function setGlobalTimezoneUser(firebaseUid: string | null) {
  useTimezoneStore.getState().setTimezoneUser(firebaseUid);
}

/** Set by UserProvider from `currentUser.timezoneOverride`. */
export function setGlobalTimezoneOverride(tz: string | null) {
  useTimezoneStore.getState().setTimezoneOverride(validTimezoneOrNull(tz));
}

/** Set by UserProvider from the current location and its timezone. */
export function setGlobalCurrentLocation(tz: string | null, selected: boolean) {
  useTimezoneStore.getState().setCurrentLocation(validTimezoneOrNull(tz), selected);
}

/** Set by TenantProvider from `tenant.defaultTimezone`. */
export function setGlobalTenantDefaultTimezone(tz: string | null) {
  useTimezoneStore.getState().setTenantDefaultTimezone(validTimezoneOrNull(tz));
}

/** Set by UserProvider from the tenant's locations. */
export function setGlobalTenantSpansTimezones(spans: boolean) {
  useTimezoneStore.getState().setTenantSpansTimezones(spans);
}

/**
 * Non-hook resolver for API files and non-component code. Mirrors the hook
 * chain: override > current location > tenant default (single-timezone
 * tenants) > browser.
 *
 * Reads committed store state, so it can never observe a tier written by a
 * render React went on to discard. On the server the tiers are always empty
 * (see the module header), so this returns UTC — a stable, host-independent
 * answer — instead of the server process zone.
 */
export function getTimezone(): string {
  const resolved = resolveTimezone(useTimezoneStore.getState());
  if (resolved) return resolved;
  return typeof window === "undefined" ? "UTC" : browserTimezone();
}

/**
 * `getTimezone()` that re-renders its component when the zone moves. Safe
 * outside the console providers (the patient portal), unlike `useTimezone`.
 */
export function useStoreTimezone() {
  const $ = _c(1);
  useTimezoneStore(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getTimezone();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Calls `listener` with the new effective zone whenever it changes. The
 * providers write several tiers in one effect, so changes are gathered into
 * one call after the current task rather than one per tier.
 */
function _temp(state) {
  return resolveTimezone(state);
}
export function onTimezoneChange(listener: (timeZone: string) => void): () => void {
  let lastZone = getTimezone();
  let scheduled = false;
  return useTimezoneStore.subscribe(() => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      const zone = getTimezone();
      if (zone === lastZone) return;
      lastZone = zone;
      listener(zone);
    });
  });
}